import React, { useRef, useState } from "react";
import { Control, Controller, Field, FieldErrors } from "react-hook-form";
import {
  StyleSheet,
  TextInput as RNTextInput,
  FlatList,
  View,
  Platform,
} from "react-native";
import Colors from "../../constants/Colors";
import { Text } from "../Themed";
import { Feather } from "@expo/vector-icons";
import Pressable from "../Pressable";

interface InputDropdownProps {
  control: Control;
  label: string;
  name: string;
  errors: FieldErrors;
  required?: boolean;
  options: { label: string; value: any }[];
  onEndReached?: () => void;
  testingID?: string;
  top?: number;
}

const InputDropdown: React.FC<InputDropdownProps> = ({
  control,
  label,
  name,
  errors,
  required = false,
  options,
  onEndReached,
  testingID,
  top = 64,
}) => {
  const [formState, setFormState] = useState<
    "default" | "active" | "error" | "selected"
  >("default");

  return (
    <View
      style={{
        width: "100%",
        zIndex: 1,
        marginBottom: 12,
      }}
    >
      <Controller
        control={control}
        rules={{
          required: required ? "This field is required" : false,
        }}
        render={({ field: { onChange, onBlur, value } }) => (
          <View style={styles.body}>
            <View style={styles.labelRow}>
              <Text style={styles.labelText}>{label}</Text>
              {required && <Text style={styles.errorStar}>*</Text>}
            </View>
            <Pressable
              hitSlop={styles.hit}
              onPress={() => {
                setFormState(formState === "active" ? "default" : "active");
              }}
              style={[
                styles.input,
                errors[name] !== undefined
                  ? styles.inputError
                  : formState === "active"
                  ? styles.inputActive
                  : null,
              ]}
              testID={testingID}
            >
              <Text style={styles.value}>{value?.label}</Text>
              <Feather
                name={formState === "active" ? "chevron-up" : "chevron-down"}
                size={20}
                color={Colors.nuetral_grey_700}
              />
            </Pressable>
            {formState === "active" &&
            (options?.length > 0 || value?.length > 0) ? (
              <View
                style={[
                  styles.inputDropdown,
                  errors[name] !== undefined
                    ? styles.inputError
                    : formState === "active"
                    ? styles.inputActive
                    : null,
                  Platform.OS === "android"
                    ? null
                    : {
                        position: "absolute",
                        top: top,
                        left: 0,
                        right: 0,
                      },
                ]}
              >
                <FlatList
                  keyboardShouldPersistTaps="handled"
                  data={
                    options.length > 0
                      ? options
                      : [
                          {
                            label: "No Results",
                            value: null,
                          },
                        ]
                  }
                  onEndReachedThreshold={0.5}
                  onEndReached={() => {
                    onEndReached && onEndReached();
                  }}
                  contentContainerStyle={styles.content}
                  nestedScrollEnabled={true}
                  keyExtractor={(item) => item?.label}
                  renderItem={({ item, index }) => (
                    <Pressable
                      onPress={() => {
                        onChange(item);
                        setFormState("selected");
                      }}
                    >
                      <Text numberOfLines={1} style={styles.itemText}>
                        {item?.label}
                      </Text>
                    </Pressable>
                  )}
                />
              </View>
            ) : null}
          </View>
        )}
        name={name}
      />
      {errors[name] ? (
        <Text style={styles.errorText}>{errors[name]?.message}</Text>
      ) : (
        <Text style={styles.space} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  body: {
    flex: 1,
  },
  content: {
    paddingVertical: 4,
    width: "100%",
  },
  itemText: {
    paddingVertical: 8,
    paddingHorizontal: 14,
  },
  loading: {
    padding: 6,
    justifyContent: "center",
    alignItems: "flex-start",
    paddingHorizontal: 16,
  },
  inputDropdown: {
    zIndex: 2,
    width: "100%",
    backgroundColor: Colors.nuetral_grey_100,
    elevation: 10,
    maxHeight: 196,
    borderRadius: 5,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: Colors.nuetral_grey_400,
  },
  input: {
    backgroundColor: Colors.nuetral_grey_100,
    width: "100%",
    borderWidth: 1,
    borderRadius: 5,
    paddingVertical: 9,
    height: 39,
    paddingHorizontal: 12,
    borderColor: Colors.nuetral_grey_400,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  labelText: {
    fontSize: 16,
    paddingBottom: 4,
  },
  errorStar: {
    fontSize: 18,
    marginBottom: -2,
    color: "#d42121",
  },
  value: {
    fontSize: 14,
    color: "black",
  },
  InputDropdown: {
    fontSize: 14,
    color: "black",
  },
  inputError: {
    borderColor: "#d42121",
  },
  inputActive: {
    borderColor: Colors.primary_500,
  },
  hit: {
    top: 10,
    bottom: 10,
    left: 10,
    right: 10,
  },
  errorText: {
    color: "#d42121",
    fontSize: 12,
    marginBottom: 10,
    height: 14,
  },
  space: {
    height: 10,
    marginBottom: 14,
  },
  labelRow: {
    flexDirection: "row",
  },
});

export default InputDropdown;
